import { computed, ref, watch, type Ref } from 'vue';

import {
  DOCUMENT_SEND_QUALITY_RECORD,
  documentSendApi,
  DocumentSendFormat,
  type DocumentSendDetail,
  type DocumentSendDetailDocument,
  type DocumentSendElectronicRecipient,
  type DocumentSendPhysicalRecipient,
  type DocumentSendRecipientType,
} from '@/entities/DocumentSend';

export interface ReadonlyField {
  label: string;
  value: string;
  wrapperClass?: string;
  boxClass?: string;
}

const defaultBoxClass =
  'flex min-h-11 items-center rounded-lg border border-gray-02 bg-gray-02/20 px-4 py-2 text-sm text-gray-07';
const centeredBoxClass = `${defaultBoxClass} justify-center`;
const emailBoxClass = `${defaultBoxClass} break-all`;

const formatLabels: Record<DocumentSendFormat, string> = {
  [DocumentSendFormat.ELECTRONIC]: 'Eletrônico',
  [DocumentSendFormat.PHYSICAL]: 'Físico',
  [DocumentSendFormat.ELECTRONIC_PHYSICAL]: 'Eletrônico e Físico',
};

const SP_DAY_FORMATTER = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const formatSendDate = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : SP_DAY_FORMATTER.format(d);
};

const recipientTypeLabels: Record<DocumentSendRecipientType, string> = {
  CLIENT: 'Cliente',
  SUPPLIER: 'Fornecedor',
};

export function useDocumentSendDetail(documentSendId: Ref<number | null>) {
  const detail = ref<DocumentSendDetail | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const documentInfo = computed(() => ({
    code: DOCUMENT_SEND_QUALITY_RECORD.code,
    revision: DOCUMENT_SEND_QUALITY_RECORD.revision,
  }));

  const summaryFields = computed<ReadonlyField[]>(() => {
    if (!detail.value) {
      return [];
    }

    return [
      {
        label: 'Título',
        value: detail.value.title,
        wrapperClass: 'min-w-0',
      },
      {
        label: 'Formato',
        value: formatLabels[detail.value.format],
        wrapperClass: 'md:w-56',
      },
    ];
  });

  const senderFields = computed<ReadonlyField[]>(() => {
    if (!detail.value) {
      return [];
    }

    return [
      {
        label: 'Responsável pelo envio',
        value: detail.value.sender.name,
      },
      {
        label: 'Cargo',
        value: detail.value.sender.position,
      },
      {
        label: 'Data de envio',
        value: formatSendDate(detail.value.sender.sendDate),
      },
    ];
  });

  function getDocumentFields(document: DocumentSendDetailDocument): ReadonlyField[] {
    return [
      {
        label: 'Prefixo',
        value: document.prefix.trim() ? document.prefix : '-',
        wrapperClass: 'md:w-40',
      },
      {
        label: 'Título',
        value: document.title ?? '-',
        wrapperClass: 'min-w-0',
      },
      {
        label: 'Revisão',
        value: String(document.revision).padStart(2, '0'),
        wrapperClass: 'md:w-24',
        boxClass: centeredBoxClass,
      },
    ];
  }

  function getElectronicRecipientFields(recipient: DocumentSendElectronicRecipient): ReadonlyField[] {
    return [
      {
        label: 'Tipo de Destinatário',
        value: recipientTypeLabels[recipient.recipientType],
        wrapperClass: 'md:w-48',
      },
      {
        label: 'Destinatário',
        value: recipient.name,
        wrapperClass: 'min-w-0',
      },
      {
        label: 'E-mail',
        value: recipient.email ?? '-',
        wrapperClass: 'md:w-64',
        boxClass: emailBoxClass,
      },
    ];
  }

  function getPhysicalRecipientFields(recipient: DocumentSendPhysicalRecipient): ReadonlyField[] {
    return [
      {
        label: 'Tipo de Destinatário',
        value: recipientTypeLabels[recipient.recipientType],
        wrapperClass: 'md:w-48',
      },
      {
        label: 'Destinatário',
        value: recipient.name,
        wrapperClass: 'min-w-0',
      },
      {
        label: 'Cópias',
        value: String(recipient.copies).padStart(2, '0'),
        wrapperClass: 'md:w-24',
        boxClass: centeredBoxClass,
      },
    ];
  }

  async function loadDocumentSendDetail(id: number): Promise<void> {
    loading.value = true;
    error.value = null;
    detail.value = null;

    try {
      const response = await documentSendApi.getDocumentSendDetail(id);
      detail.value = response.data;
    } catch {
      error.value = 'Erro ao carregar documento de controle de envio';
    } finally {
      loading.value = false;
    }
  }

  function handleRetry(): void {
    if (documentSendId.value !== null) {
      void loadDocumentSendDetail(documentSendId.value);
    }
  }

  watch(
    documentSendId,
    (id) => {
      if (id === null) {
        detail.value = null;
        error.value = null;
        loading.value = false;
        return;
      }

      void loadDocumentSendDetail(id);
    },
    { immediate: true }
  );

  return {
    detail,
    loading,
    error,
    handleRetry,
    documentInfo,
    summaryFields,
    senderFields,
    getDocumentFields,
    getElectronicRecipientFields,
    getPhysicalRecipientFields,
  };
}
